/**
 * Shared markdown utility functions used by both the MarkdownContent renderer
 * and app-level code (plain-text previews, image URL extraction, etc.).
 *
 * All functions are pure with no external dependencies.
 */

/**
 * Reverses the link-escaping artifact produced by MDXEditor when a raw
 * markdown link is typed as plain text instead of created via the toolbar.
 * MDXEditor escapes both `[` and `(`, e.g. `\[text]\(url)`. This restores
 * them to valid markdown. Only fires when BOTH brackets are escaped together
 * (the exact bug signature) so a lone intentional `\[` is left untouched.
 */
export function unescapeMarkdownLinks(markdown: string): string {
  if (!markdown) return markdown;
  return markdown.replace(
    /(!?)\\\[([^\]]*)\]\\\(([^)]*)\)/g,
    (_match, bang: string, text: string, target: string) =>
      `${bang}[${text}](${target})`
  );
}

// A whole markdown link or image, matched first so the URLs in its label and
// target are left alone, or else a plain URL.
const MARKDOWN_LINK_OR_PLAIN_URL =
  /(!?\[[^\]]*\]\([^)]*\))|(?<!\]\()(?<!\])(?<!\))(?<!["'])\b(https?:\/\/[^\s<>"')]+)\b/g;

/**
 * Turns each plain URL into a markdown link. URLs that are already part of a
 * markdown link, including a label that is the URL itself
 * (`[https://x.com](https://x.com)`), are left as they are.
 */
export function linkifyPlainUrls(markdown: string): string {
  if (!markdown) return markdown;
  return markdown.replace(
    MARKDOWN_LINK_OR_PLAIN_URL,
    (match, link: string | undefined, url: string | undefined) =>
      link ? match : `[${url}](${url})`
  );
}

/**
 * Escapes ordered-list markers (`1. `, `2) `) so CommonMark treats those
 * lines as paragraphs. Extra lines under each number then stay as normal
 * text instead of splitting/resetting a markdown `<ol>`. Nested markers
 * like `   2. test` are pulled up to the left so they do not stay indented
 * under the previous item.
 *
 * Lines inside fenced code blocks are left verbatim.
 */
export function escapeOrderedListMarkers(markdown: string): string {
  if (!markdown) return markdown;

  const lines = markdown.split('\n');
  const out: string[] = [];
  let inFence = false;
  let fence = '';

  for (const line of lines) {
    const fenceMatch = line.match(/^[ \t]*(`{3,}|~{3,})/);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fence = fenceMatch[1];
      } else if (line.trim().startsWith(fence)) {
        inFence = false;
        fence = '';
      }
      out.push(line);
      continue;
    }
    if (!inFence) {
      // Drop leading indent so a nested `   2. test` becomes a top-level
      // paragraph instead of staying under the previous item.
      out.push(line.replace(/^(\s*)(\d{1,9})([.)])(\s+)/, '$2\\$3$4'));
      continue;
    }
    out.push(line);
  }

  return out.join('\n');
}

/**
 * Inserts a blank line before a standalone thematic break (`---` or `===`)
 * that immediately follows non-blank content, preventing CommonMark from
 * treating it as a Setext heading underline.
 *
 * Lines inside fenced code blocks are left verbatim.
 */
export function ensureBlankLineBeforeThematicBreak(markdown: string): string {
  const lines = markdown.split('\n');
  const out: string[] = [];
  let inFence = false;
  let fence = '';

  const isThematicBreak = (line: string) =>
    /^[ \t]*(?:-{3,}|={3,})[ \t]*\r?$/.test(line);

  for (const line of lines) {
    const fenceMatch = line.match(/^[ \t]*(`{3,}|~{3,})/);
    if (fenceMatch) {
      if (!inFence) {
        inFence = true;
        fence = fenceMatch[1];
      } else if (line.trim().startsWith(fence)) {
        inFence = false;
        fence = '';
      }
      out.push(line);
      continue;
    }
    if (!inFence && isThematicBreak(line)) {
      const prev = out[out.length - 1];
      if (prev !== undefined && prev.trim() !== '') {
        out.push('');
      }
    }
    out.push(line);
  }
  return out.join('\n');
}

const NAMED_HTML_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: '\u00A0'
};

/**
 * Decodes HTML character references that MDXEditor embeds in serialised
 * markdown to preserve whitespace (e.g. `&#x20;` for a trailing space after
 * bold text). Plain-text previews (`remove-markdown`) leave these entities
 * visible; this restores the intended characters without using `innerHTML`.
 */
export function decodeHtmlEntities(text: string): string {
  if (!text) return text;
  return text
    .replace(/&#x([0-9a-fA-F]+);/g, (_match, hex: string) => {
      const code = Number.parseInt(hex, 16);
      return Number.isNaN(code) ? _match : String.fromCodePoint(code);
    })
    .replace(/&#(\d+);/g, (_match, dec: string) => {
      const code = Number.parseInt(dec, 10);
      return Number.isNaN(code) ? _match : String.fromCodePoint(code);
    })
    .replace(
      /&(amp|lt|gt|quot|apos|nbsp);/g,
      (_match, name: string) => NAMED_HTML_ENTITIES[name] ?? _match
    );
}

/**
 * Extracts image URLs from a markdown string.
 * Returns an array of `{ url }` objects matching every `![alt](url)` token.
 */
export function extractMarkdownImageUrls(
  markdown: string
): { url: string }[] {
  if (!markdown) return [];
  const urls: { url: string }[] = [];
  markdown.replace(/!\[.*?\]\((.*?)\)/g, (_, url: string) => {
    urls.push({ url });
    return '';
  });
  return urls;
}
